<template>
  <div class="team-events card">
    <div v-if="isAdmin && isUserLoggedIn">
      <add-new-event :team-id="teamId" :activity-id="activityId" />
    </div>

    <div class="team-events__list" v-if="sortedEventArray.length > 0">
      <div v-for="(event, index) in sortedEventArray" :key="event.eventId">
        <div class="team-events__item">
          <el-avatar class="team-events__item-avatar" :size="50">
            <activity-image :activity="event.activityId" :alt="event.name" />
          </el-avatar>

          <router-link
            :to="{ name: 'EventPage', params: { link: event.link } }"
            class="team-events__item-info team-events__item-info--link"
            v-if="isUserLoggedIn"
          >
            <div class="team-events__item-heading">
              <span class="team-events__item-title">
                {{ event.name }}
              </span>
              <span class="team-events__item-date">
                {{ formatDate(event.startDate, SHORT_DATETIME_FORMAT) }}
              </span>
            </div>
            <div class="team-events__item-description">
              {{ event.eventType }}
            </div>
          </router-link>

          <div class="team-events__item-info" v-if="!isUserLoggedIn">
            <div class="team-events__item-heading">
              <span class="team-events__item-title">
                {{ event.name }}
              </span>
              <span class="team-events__item-date">
                {{ formatDate(event.startDate, SHORT_DATETIME_FORMAT) }}
              </span>
            </div>
            <div class="team-events__item-description">
              {{ event.eventType }}
            </div>
          </div>

          <div
            class="team-events__item-delete"
            v-if="isAdmin && isUserLoggedIn"
          >
            <el-button plain type="danger" @click="handleDeleteEvent(event)">
              Delete
            </el-button>
          </div>
        </div>
        <el-divider v-if="index < sortedEventArray.length - 1" />
      </div>
    </div>

    <div class="team-events__empty-text" v-if="sortedEventArray.length === 0">
      No events
    </div>

    <div class="team-events__search-label" v-if="isAdmin && isUserLoggedIn">
      Event
    </div>
    <el-form
      :model="eventSearchForm"
      :rules="eventSearchRules"
      label-position="top"
      ref="eventSearchForm"
      class="team-events__search"
      v-if="isAdmin && isUserLoggedIn"
    >
      <el-form-item prop="name" class="team-events__search-input">
        <el-autocomplete
          class="wide"
          v-model="eventSearchForm.name"
          :fetch-suggestions="fetchEvents"
          placeholder="Start typing event name"
          value-key="name"
          :trigger-on-focus="false"
          :debounce="500"
          @select="handleSelectEvent"
        ></el-autocomplete>
      </el-form-item>

      <el-button
        @click="requestAddEventToTeam"
        type="primary"
        :disabled="!currentEvent || isButtonDisabled('add-event')"
      >
        Add
      </el-button>
    </el-form>

    <el-dialog
      v-model="isDeleteEventConfirmationVisible"
      width="30%"
      title="Confirmation"
    >
      <span v-if="eventToDelete">
        Are you sure you want to delete {{ eventToDelete.name }} from this team?
      </span>
      <template #footer>
        <span class="dialog-footer">
          <el-button
            plain
            type="primary"
            @click="isDeleteEventConfirmationVisible = false"
          >
            Cancel
          </el-button>
          <el-button
            type="danger"
            @click="requestDeleteEvent"
            :disabled="isButtonDisabled('delete-event')"
          >
            Delete
          </el-button>
        </span>
    </template>
    </el-dialog>
  </div>
</template>

<script>
import AddNewEvent from "@/components/events/AddNewEvent.vue";
import { addEventToTeam, removeEventFromTeam } from "@/api/team.js";
import { searchEvent } from "@/api/events.js";
import { showMessage } from "@/utils/messages.js";
import {
  SHORT_DATETIME_FORMAT,
  formatDate,
  parseErrorsByFields,
} from "@/utils/index.js";
import { disableButton } from "@/mixins/disableButton.js";
import ActivityImage from "@/components/common/ActivityImage.vue";

export default {
  components: {
    ActivityImage,
    AddNewEvent,
  },
  name: "TeamEvents",
  props: {
    teamId: {
      type: Number,
      required: true,
    },
    activityId: {
      type: Number,
      required: false,
    },
    eventArray: {
      type: Array,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      required: false,
    },
    isUserLoggedIn: {
      type: Boolean,
      required: false,
    },
  },
  mixins: [disableButton],
  data() {
    return {
      eventSearchForm: { name: null },
      eventSearchErrors: { name: null },
      eventSearchRules: { name: [{ required: false }] },
      currentEvent: null,
      SHORT_DATETIME_FORMAT,
      isDeleteEventConfirmationVisible: false,
      eventToDelete: null,
    };
  },
  computed: {
    sortedEventArray() {
      // Sort events in descending order by startDate
      return [...this.eventArray].sort(
        (a, b) => new Date(b.startDate) - new Date(a.startDate)
      );
    },
    searchEventData() {
      const { name } = this.eventSearchForm;
      return {
        skip: 0,
        limit: 1000,
        filterModel: {
          name,
        },
      };
    },
  },
  methods: {
    showMessage,
    formatDate,
    parseErrorsByFields,
    async fetchEvents(name, callback) {
      if (name !== "") {
        const { searchEventData } = this;
        const { data, success, errors } = await searchEvent(searchEventData);

        if (success) {
          callback(data.items);
        } else if (errors) {
          this.eventSearchForm = {
            ...this.eventSearchForm,
            ...this.parseErrorsByFields(errors),
          };
          callback([]);
        }
      }
    },
    async requestDeleteEvent() {
      this.disableButton("delete-event");

      const { teamId } = this;
      const { eventId } = this.eventToDelete;
      const { success } = await removeEventFromTeam({ teamId, eventId });
      this.enableButton("delete-event");

      if (success) {
        this.showMessage({
          type: "success",
          message: "Event has been removed",
        });
        this.eventToDelete = null;
        this.isDeleteEventConfirmationVisible = false;
        this.$emit("update-data");
      }
    },
    async requestAddEventToTeam() {
      this.disableButton("add-event");

      const { teamId } = this;
      const { eventId } = this.currentEvent;
      const { success } = await addEventToTeam({
        teamId,
        eventId,
      });
      this.enableButton("add-event");

      if (success) {
        this.eventSearchForm.name = null;
        this.currentEvent = null;
        this.showMessage({
          type: "success",
          message: "Event has been added",
        });
        this.$emit("update-data");
      }
    },
    handleDeleteEvent(event) {
      this.eventToDelete = event;
      this.isDeleteEventConfirmationVisible = true;
    },
    handleSelectEvent(event) {
      this.currentEvent = event;
    },
  },
};
</script>

<style lang="scss">
.team-events {
  $this: &;

  &__emty-text {
  }

  &__list {
  }
  &__item {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
  }

  &__item-avatar {
    margin-right: var(--global-dimension-size-125);
    width: 50px;
  }

  &__item-info {
    flex-grow: 1;

    &--link {
      &:hover {
        #{$this}__item-title {
          color: var(--color-primary-400);
        }
      }
    }
  }

  &__item-heding {
  }

  &__item-title {
    margin-right: var(--global-dimension-size-125);
    font-weight: var(--global-font-weight-medium);
    font-size: var(--font-size-300);
  }

  &__item-date {
    font-size: var(--font-size-200);
  }

  &__item-description {
    font-size: var(--font-size-200);
  }

  &__item-delete {
  }

  &__search-label {
    margin-bottom: var(--global-dimension-size-65);

    * + & {
      margin-top: var(--global-dimension-size-350);
    }
  }

  &__search {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-start;
  }

  &__search-input {
    margin-right: var(--global-dimension-size-250);
    margin-bottom: 0;
    flex-grow: 1;
  }
}
</style>
